import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Download, FileDown, Loader2, Lock, TriangleAlert } from 'lucide-react';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { captureError } from '@/lib/logger';

import AgreementMarkdown from './AgreementMarkdown';
import MergePreflight from './MergePreflight';
import {
    fetchDocumentBody,
    previewAgreementPdf,
    renderAgreement,
    saveBlob,
} from '@/lib/agreements/api';
import { parseAgreementError } from '@/lib/agreements/constants';

interface AgreementReviewSheetProps {
    documentId: number | null;
    onOpenChange: (open: boolean) => void;
    /** The pre-flight's destination — with the merge field, for a host that routes by it. */
    onDeepLink: (step: string, field?: string) => void;
    /** Opens the finalized PDF from storage. Absent while the document is still in flight. */
    onDownloadFinal?: () => void;
}

/**
 * The document under review, rendered from the same markdown the PDF is set from.
 *
 * WHAT IS ON SCREEN IS WHAT IS HASHED. Before the freeze the text is re-resolved on every
 * call, so an admin correcting an address sees the correction; after it, `agreement_render`
 * returns the frozen bytes verbatim and the header says so. The `content_sha256` shown at
 * the foot is the value a signature attests to — it is displayed rather than hidden so a
 * "the contract changed under me" refusal has something to compare against.
 */
export default function AgreementReviewSheet({
    documentId,
    onOpenChange,
    onDeepLink,
    onDownloadFinal,
}: AgreementReviewSheetProps) {
    const [highlight, setHighlight] = useState(false);
    const [exporting, setExporting] = useState(false);

    const { data: render, isLoading } = useQuery({
        queryKey: ['agreement-render', documentId],
        queryFn: () => renderAgreement(documentId as number),
        enabled: !!documentId,
    });

    // Only fetched when the toggle is on: the body is the template, not the document, and
    // nothing else on this screen needs it.
    const { data: templateBody } = useQuery({
        queryKey: ['agreement-document-body', documentId],
        queryFn: () => fetchDocumentBody(documentId as number),
        enabled: !!documentId && highlight,
        staleTime: 5 * 60 * 1000,
    });

    const handleExport = async () => {
        if (!documentId || !render) return;
        setExporting(true);
        try {
            const blob = await previewAgreementPdf(documentId, highlight);
            saveBlob(blob, `${render.reference_no}-preview.pdf`);
        } catch (error) {
            captureError(error);
            toast.error(parseAgreementError(error).message);
        } finally {
            setExporting(false);
        }
    };

    const missing = render?.missing ?? [];

    return (
        <Sheet open={!!documentId} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="!inset-y-4 !right-4 !h-auto !w-[58vw] !max-w-none rounded-2xl flex flex-col shadow-2xl border bg-background"
            >
                <SheetHeader className="pr-8">
                    <SheetTitle className="flex flex-wrap items-center gap-2">
                        {render?.title ?? 'Agreement'}
                        {render?.frozen && (
                            <Badge variant="outline" className="gap-1 font-normal">
                                <Lock className="h-3 w-3" />
                                Frozen
                            </Badge>
                        )}
                        {render?.drifted && (
                            <Badge variant="outline" className="gap-1 border-amber-500/40 font-normal text-amber-700 dark:text-amber-400">
                                <TriangleAlert className="h-3 w-3" />
                                Contract has changed since the freeze
                            </Badge>
                        )}
                    </SheetTitle>
                    {render && (
                        <p className="text-sm text-muted-foreground">
                            {render.reference_no}
                            {render.frozen_at && ` · frozen ${format(new Date(render.frozen_at), 'MMM d, yyyy · HH:mm')}`}
                        </p>
                    )}
                </SheetHeader>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-b pb-3">
                    <div className="flex items-center gap-2">
                        <Switch id="highlight-merge" checked={highlight} onCheckedChange={setHighlight} />
                        <Label htmlFor="highlight-merge" className="cursor-pointer text-sm font-normal">
                            Highlight merge fields
                        </Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || !render}>
                            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
                            Export PDF
                        </Button>
                        {onDownloadFinal && render?.final_path && (
                            <Button size="sm" onClick={onDownloadFinal}>
                                <Download className="mr-2 h-4 w-4" />
                                Download executed PDF
                            </Button>
                        )}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto py-4">
                    {isLoading || !render ? (
                        <div className="space-y-3">
                            <Skeleton className="h-6 w-2/3" />
                            <Skeleton className="h-40 w-full" />
                            <Skeleton className="h-40 w-full" />
                        </div>
                    ) : (
                        <>
                            {missing.length > 0 && (
                                <div className="mb-4">
                                    <MergePreflight missing={missing} onDeepLink={onDeepLink} />
                                </div>
                            )}
                            {highlight && !templateBody && (
                                <p className="mb-3 text-xs text-muted-foreground">
                                    Loading the template body to mark every field…
                                </p>
                            )}
                            <AgreementMarkdown
                                renderedMd={render.rendered_md}
                                values={render.values}
                                parties={render.parties ?? []}
                                templateBody={templateBody}
                                highlight={highlight}
                                className="mx-auto max-w-3xl"
                            />
                        </>
                    )}
                </div>

                {render && (
                    <div className="border-t pt-3">
                        <p className="break-all font-mono text-[11px] text-muted-foreground">
                            content_sha256 {render.content_sha256}
                        </p>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
